import {
  autoUpdate,
  computePosition,
  flip,
  offset as offsetMiddleware,
  shift,
  size,
  type Middleware,
  type Placement,
} from "@floating-ui/dom";
import { useEffect } from "react";

export interface UseAnchorPositionOptions {
  /** Preferred side and alignment; flip may pick the opposite side. Defaults to "bottom-start". */
  placement?: Placement;
  /** Gap in px between anchor and floating element. Defaults to 6. */
  offset?: number;
  /** Pin the floating element's width to the anchor's (select/combobox menus). */
  matchWidth?: boolean;
  /** Position and track only while true - pass the open state here. */
  enabled: boolean;
}

/**
 * Positions a floating element against an anchor with floating-ui and keeps it
 * there (scroll, resize, layout shifts) for as long as `enabled` is true.
 *
 * Writes inline `position: fixed` plus left/top and zeroes the UA popover margin.
 * The fixed strategy is deliberate: top-layer elements still resolve their
 * coordinates against the viewport, so viewport-relative values are the correct
 * ones for both popovers and plain absolutely-positioned surfaces.
 */
export function useAnchorPosition(
  anchorRef: React.RefObject<HTMLElement | null>,
  floatingRef: React.RefObject<HTMLElement | null>,
  {
    placement = "bottom-start",
    offset = 6,
    matchWidth = false,
    enabled,
  }: UseAnchorPositionOptions,
): void {
  useEffect(() => {
    if (!enabled) return;
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor || !floating) return;

    const middleware: Middleware[] = [
      offsetMiddleware(offset),
      flip(),
      shift({ padding: 8 }),
    ];
    if (matchWidth) {
      middleware.push(
        size({
          apply({ rects, elements }) {
            elements.floating.style.width = `${rects.reference.width}px`;
          },
        }),
      );
    }

    let cancelled = false;
    const stop = autoUpdate(anchor, floating, () => {
      void computePosition(anchor, floating, {
        placement,
        strategy: "fixed",
        middleware,
      }).then(({ x, y }) => {
        if (cancelled) return;
        floating.style.position = "fixed";
        floating.style.left = `${x}px`;
        floating.style.top = `${y}px`;
        floating.style.margin = "0";
      });
    });

    return () => {
      cancelled = true;
      stop();
    };
  }, [anchorRef, floatingRef, enabled, placement, offset, matchWidth]);
}
